import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader/PageHeader';
import { getMyCompanySupportMessages, sendMyCompanySupportMessage } from '../../services/CourierSupportService';

const SupportPage = () => {
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const scrollMessagesToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
    }
    messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' });
  }, []);

  const loadMessages = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const data = await getMyCompanySupportMessages();
      setMessages(Array.isArray(data) ? data : []);
      requestAnimationFrame(() => scrollMessagesToBottom());
    } catch (err) {
      console.error('Error loading company support messages:', err);
      setError('No se pudieron cargar los mensajes de soporte.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [scrollMessagesToBottom]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      loadMessages(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    setError('');
    try {
      const created = await sendMyCompanySupportMessage(text);
      if (created?.id != null) {
        setMessages((prev) => (prev.some((item) => item.id === created.id) ? prev : [...prev, created]));
      }
      setDraft('');
      await loadMessages(true);
    } catch (err) {
      console.error('Error sending company support message:', err);
      setError('No se pudo enviar el mensaje.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Soporte"
        subtitle="Escribinos por acá y nuestro equipo te responde por este mismo chat."
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mt: 2, minHeight: '68vh', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Chat de soporte</Typography>
          <Typography variant="body2" color="text.secondary">Canal exclusivo para consultas operativas de tu cuenta.</Typography>
        </CardContent>
        <Divider />

        <Box ref={messagesContainerRef} sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : messages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Aún no hay mensajes. Escribí el primero para iniciar la conversación.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {messages.map((message) => {
                const isCompany = message.senderType === 'COMPANY';
                return (
                  <Box
                    key={message.id}
                    sx={{
                      alignSelf: isCompany ? 'flex-end' : 'flex-start',
                      maxWidth: '72%',
                      bgcolor: isCompany ? '#E8EBFF' : '#F5F7FA',
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Typography variant="body2">{message.message}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {dayjs(message.createdAt).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  </Box>
                );
              })}
              <Box ref={messagesEndRef} />
            </Stack>
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Escribí tu consulta"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={sending}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
          />
          <LoadingButton variant="contained" loading={sending} onClick={handleSend} disabled={!draft.trim()}>
            Enviar
          </LoadingButton>
        </Box>
      </Card>

    </Box>
  );
};

export default SupportPage;
